import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QueryCompositeFilterConstraint } from "firebase/firestore";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import {
  createCompositeFilterForMedicalMyPaOrdersTables,
  createOrderByForMedicalPaTables,
} from "../../../utils/createCompositeFilterForTables";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  medicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  medicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const myMedicalPaOrdersSlice = createSlice({
  name: "myMedicalPaOrders",
  initialState,
  reducers: {
    setMedicalPaOrders: (state, action: PayloadAction<MedicalPaOrder[]>) => {
      state.medicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },
    appendMedicalPaOrders: (state, action: PayloadAction<MedicalPaOrder[]>) => {
      state.medicalPaOrders = state.medicalPaOrders
        ? [...state.medicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.medicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },

    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },
    resetPagination: (state) => {
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.medicalPaOrders = null;
      state.totalDocuments = 0;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
    setLoadingState: (state, action) => {
      state.loading = action.payload;
    },
  },
});

export const {
  setMedicalPaOrders,
  appendMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
  setLoadingState,
} = myMedicalPaOrdersSlice.actions;

export const fetchTotalDocumentsCount =
  (facilityId: string, providerId: string, searchTerm?: string) =>
  async (dispatch) => {
    try {
      const allDocsCount =
        await FirestoreService.getAllDocumentCountByCompositeFilter(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalMyPaOrdersTables(
            facilityId,
            providerId,
            searchTerm,
          ) as QueryCompositeFilterConstraint,
          [createOrderByForMedicalPaTables()],
        );

      dispatch(setTotalDocuments(allDocsCount));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      dispatch(setTotalDocuments(0));
      return 0;
    }
  };

export const fetchMyMedicalPaOrdersAfterSearch =
  (facilityId: string, providerId: string, searchTerm?: string) =>
  async (dispatch, getState) => {
    const { myMedicalPaOrders } = getState();
    if (myMedicalPaOrders.loading) return;

    dispatch(setLoading(true));

    try {
      const data =
        await FirestoreService.fetchQueryCollectionByCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          createCompositeFilterForMedicalMyPaOrdersTables(
            facilityId,
            providerId,
            searchTerm,
          ) as QueryCompositeFilterConstraint,
          [createOrderByForMedicalPaTables()],
        );

      dispatch(setMedicalPaOrders(data as MedicalPaOrder[]));
      dispatch(fetchTotalDocumentsCount(facilityId, providerId, searchTerm));
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchMyMedicalPaOrders =
  (facilityId: string, providerId: string, pageSize: number) =>
  async (dispatch, getState) => {
    const { myMedicalPaOrders } = getState();

    if (myMedicalPaOrders.loading) {
      return;
    }

    dispatch(setLoading(true));
    dispatch(resetPagination());

    try {
      FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(fetchTotalDocumentsCount(facilityId, providerId));
        },
        (error) => {
          logDataToConsole("Error fetching data", error);
          dispatch(setError(error.message));
        },
        undefined,
        createCompositeFilterForMedicalMyPaOrdersTables(
          facilityId,
          providerId,
        ) as QueryCompositeFilterConstraint,
        [createOrderByForMedicalPaTables()],
      );
    } catch (error) {
      logDataToConsole("Error in fetchMyMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMoreMyMedicalPaOrders =
  (facilityId: string, providerId: string, pageSize: number) =>
  async (dispatch, getState) => {
    const { myMedicalPaOrders } = getState();

    if (
      myMedicalPaOrders.loading ||
      myMedicalPaOrders.showinLineLoader ||
      myMedicalPaOrders.currentlyLoadedCount ===
        myMedicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    try {
      const unsubscribe =
        FirestoreService.paginateQueryWithCompositeFilter<MedicalPaOrder>(
          FirestoreCollectionReference.medicalPaOrders(),
          pageSize,
          ({ docs, lastVisible }) => {
            dispatch(appendMedicalPaOrders(docs));
            dispatch(setLastVisible(lastVisible));
            dispatch(setinLineLoader(false));
          },
          (error) => {
            logDataToConsole("Error fetching more data", error);
            dispatch(setError(error.message));
            dispatch(setinLineLoader(false));
          },
          myMedicalPaOrders.lastVisible,
          createCompositeFilterForMedicalMyPaOrdersTables(
            facilityId,
            providerId,
          ) as QueryCompositeFilterConstraint,
          [createOrderByForMedicalPaTables()],
        );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { myMedicalPaOrders } = state;

  return (
    myMedicalPaOrders.currentlyLoadedCount < myMedicalPaOrders.totalDocuments &&
    endIndex === myMedicalPaOrders.currentlyLoadedCount - 1
  );
};

export default myMedicalPaOrdersSlice.reducer;
